import { lazy, Suspense } from "react";
import ConfigService from "@/services/ConfigService";
import EmptyState from "@/components/emptyState/EmptyState";
import emptyStateImg from "@/assets/img/emptyCasinoIcon.png";
import { ConfigProvider } from "@/providers/ConfigProvider";
import { GamesProvider } from "@/providers/GamesProvider";

// Lazy load CasinoWidget only when promoId is valid - avoids CSS preload errors
const CasinoWidget = lazy(() => import("./CasinoWidget"));
const MultiJackpotWidget = lazy(
  () => import("./subWidgets/jackpotWidget/standalone/MultiJackpotWidget")
);

export type CasinoWidgetMode = "casino" | "badgeEndless";

const CasinoWidgetGuard = ({ mode = "casino" }: { mode?: CasinoWidgetMode }) => {
  const promoId = mode === "casino" ? ConfigService.getPromoId() : "badge-endless";
  const gameId = ConfigService.getGameId();
  const providerId = ConfigService.getProviderId();

  // Multi-jackpot mode: both gameId and providerId without promoId — skip ConfigProvider/CasinoWidget entirely
  if (mode === "casino" && gameId && providerId && !promoId) {
    return (
      <Suspense fallback={null}>
        <MultiJackpotWidget gameId={gameId} providerId={providerId} />
      </Suspense>
    );
  }

  // Check promoId
  if (mode === "casino" && !promoId) {
    return (
      <EmptyState
        img={emptyStateImg}
        title="Initialization Failed"
        text="SDK initialization failed. Missing promoId parameter."
      />
    );
  }

  return (
    <Suspense fallback={null}>
      {mode === "casino" ? (
        <ConfigProvider>
          <GamesProvider>
            <CasinoWidget source="casino" />
          </GamesProvider>
        </ConfigProvider>
      ) : (
        <GamesProvider>
          <CasinoWidget source="badge" />
        </GamesProvider>
      )}
    </Suspense>
  );
};

export default CasinoWidgetGuard;
